// apps/admin/src/components/composer/layers-panel.tsx
//
// P5 UI Polish W2 T4 — Layers panel (spec §3.1, §3.2).
// P5 UI Polish W5 T6 — Sortable reorder via @dnd-kit/sortable (spec §2.2).
//
// Lists zones with click-to-select + a lock toggle per row. Rows can be
// dragged by their grip handle to reorder zones; array order = render order
// (later index = on top in z-stack). Marquee selection (drag rectangle on
// empty canvas to select multiple zones) is deferred to a future polish wave.

import type { KeyboardEvent, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { DndContext, closestCenter, type DragEndEvent } from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { Zone } from '@displaycms/contracts';
import { Icon } from '@/components/icon';

export interface LayersPanelProps {
  zones: ReadonlyArray<Zone>;
  selectedIds: ReadonlyArray<string>;
  lockedIds: ReadonlyArray<string>;
  onSelect: (id: string, shiftKey: boolean) => void;
  onLockToggle: (id: string) => void;
  onReorder: (zones: Zone[]) => void;
}

interface SortableLayerRowProps {
  zone: Zone;
  isSelected: boolean;
  isLocked: boolean;
  onSelect: (shiftKey: boolean) => void;
  onLockToggle: () => void;
}

function SortableLayerRow({
  zone,
  isSelected,
  isLocked,
  onSelect,
  onLockToggle,
}: SortableLayerRowProps): JSX.Element {
  const { t } = useTranslation(['composers']);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: zone.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      data-testid={`layer-row-${zone.id}`}
      data-selected={isSelected}
      data-locked={isLocked}
      className={[
        'flex items-center gap-1 rounded-md border px-1 py-1.5 text-xs',
        isSelected
          ? 'border-primary bg-accent'
          : 'border-border bg-background hover:bg-accent/40',
      ].join(' ')}
    >
      {/* Drag handle — ONLY drag activator; click on it does NOT trigger select */}
      <button
        type="button"
        {...attributes}
        {...listeners}
        data-testid={`layer-drag-${zone.id}`}
        aria-label={t('composers:layer_drag_aria', {
          defaultValue: 'Glisser pour réordonner',
        })}
        className="cursor-grab text-muted-foreground hover:text-foreground active:cursor-grabbing"
        onClick={(e: MouseEvent) => e.stopPropagation()}
      >
        <Icon name="grip-vertical" size={14} />
      </button>

      {/* Zone label — click triggers select */}
      <button
        type="button"
        className="flex-1 truncate text-left font-medium"
        style={{ color: isLocked ? 'var(--muted-foreground)' : undefined }}
        onClick={(e: MouseEvent) => onSelect(e.shiftKey)}
        onKeyDown={(e: KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSelect(e.shiftKey);
          }
        }}
      >
        {zone.id}
      </button>

      {/* Lock toggle */}
      <button
        type="button"
        data-testid={`lock-toggle-${zone.id}`}
        onClick={(e: MouseEvent) => {
          e.stopPropagation();
          onLockToggle();
        }}
        aria-label={
          isLocked
            ? t('composers:unlock_zone', { defaultValue: 'Déverrouiller' })
            : t('composers:lock_zone', { defaultValue: 'Verrouiller' })
        }
        className="text-xs text-muted-foreground hover:text-foreground"
      >
        {isLocked ? '🔒' : '🔓'}
      </button>
    </li>
  );
}

export function LayersPanel(props: LayersPanelProps): JSX.Element {
  const { t } = useTranslation(['composers']);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = props.zones.findIndex((z) => z.id === active.id);
    const newIdx = props.zones.findIndex((z) => z.id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    props.onReorder(arrayMove([...props.zones], oldIdx, newIdx));
  };

  return (
    <aside
      data-testid="layers-panel"
      className="flex flex-col gap-2"
    >
      <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {t('composers:layers_panel_title', { defaultValue: 'Calques' })}
      </h3>
      {props.zones.length === 0 ? (
        <p
          data-testid="layers-panel-empty"
          className="rounded-md border border-dashed border-border p-3 text-center text-xs text-muted-foreground"
        >
          {t('composers:layout_editor.no_zones')}
        </p>
      ) : (
        <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext
            items={props.zones.map((z) => z.id)}
            strategy={verticalListSortingStrategy}
          >
            <ul className="flex flex-col gap-1">
              {props.zones.map((z) => (
                <SortableLayerRow
                  key={z.id}
                  zone={z}
                  isSelected={props.selectedIds.includes(z.id)}
                  isLocked={props.lockedIds.includes(z.id)}
                  onSelect={(shiftKey) => props.onSelect(z.id, shiftKey)}
                  onLockToggle={() => props.onLockToggle(z.id)}
                />
              ))}
            </ul>
          </SortableContext>
        </DndContext>
      )}
    </aside>
  );
}
